import { useState, useEffect, useRef, createElement as rc } from 'react';
import PropTypes from 'prop-types';
import useEventSink from '../../hooks/useEventSink';
import logging from '@sstdev/lib_logging';
import lodash from 'lodash';
const { isEqual } = lodash;
import { pageLoadStorage, contextMatching } from 'lib_ui-services';
import { ActiveRecordContext } from './ActiveRecordContext';
import {
    createFormInstanceId,
    getDetailLifecycleEvent,
    getNextActiveRecord,
    shouldIgnoreActiveRecordEvent
} from './activeRecordLifecycle';

function ActiveRecord(props) {
    const { namespace, relation, type, children, isNew, record, activationVerb, currentRoute } = props || {};
    if (namespace == null || relation == null) {
        logging.error('[ACTIVE_RECORD] Namespace and relation are required properties for ActiveRecord');
    }
    const [activeRecord, setActiveRecord] = useState(() => ({
        namespace,
        relation,
        type,
        isNew,
        record,
        activationVerb,
        formInstanceId: createFormInstanceId()
    }));
    // Dirty-form confirmation can finish after the user opens another form. The
    // callback reads this ref again before changing the displayed record.
    const activeRecordRef = useRef(activeRecord);
    const [subscribe, publish, request] = useEventSink();

    useEffect(() => {
        if (props.type && activeRecord.type !== props.type) {
            const updatedActiveRecord = { ...activeRecord, type: props.type };

            if (updatedActiveRecord?.record == null) {
                logging.debug('[ACTIVE_RECORD] removed.');
            } else {
                logging.debug(
                    `[ACTIVE_RECORD] id: ${updatedActiveRecord.record._id}, titular: ${
                        updatedActiveRecord.record.title ?? updatedActiveRecord.record.assetNo
                    }.`
                );
            }
            activeRecordRef.current = updatedActiveRecord;
            setActiveRecord(updatedActiveRecord);
        }
    }, [activeRecord, props.type]);

    // If the rules engine publishes a success for one of these verbs, then the active record
    // will be changing.
    useEffect(() => {
        let allowStateChange = true;
        const unsubscribes = ['copy', 'edit', 'view', 'new', 'cancel', 'submit', 'remove'].map(verb =>
            subscribe({ verb, namespace, relation, type, status: 'success' }, async (payload, context) => {
                /**
                 * @typedef payload
                 * @property {string} namespace "should" match what was configured in props
                 * @property {string} relation "should" match what was configured in props
                 * @property {string} type "should" match what was configured in props, though props _can_ change that
                 * @property {string} isNew "should" match what was configured in props?
                 * @property {object} [record] THIS is the actual new active record
                 * @property {string} [activationVerb]
                 */

                // If this event has a formInstanceId for a form that is no longer
                // displayed, ignore it before it can prompt for confirmation or change
                // ActiveRecord.
                const activeRecordAtStart = activeRecordRef.current;
                if (shouldIgnoreActiveRecordEvent(activeRecordAtStart, payload, context, verb)) return;

                //basic validation, if this get's messed up, it becomes really confusing
                if (
                    (payload.namespace && payload.namespace !== namespace) ||
                    (payload.relation && payload.relation !== relation)
                ) {
                    logging.warn(
                        `[ACTIVE_RECORD] Invalid payload. Expected ${namespace}:${relation}, got ${payload.namespace}:${payload.relation}  `
                    );
                }
                // Confirm active record change.  If nothing cares (no subscribers) this request will just
                // resolve without an error or timeout.  As of 2022-03-14 this is only consumed by useFormMachine
                try {
                    // Cancellation of a redirect reboundEvent cannot be subject to confirmation
                    const reboundEvents = pageLoadStorage.getKey(`reboundEvents|${currentRoute}`) ?? [];
                    const isReboundEvent = reboundEvents.some(re => contextMatching(re, context));
                    //Single Record forms are re-initialized right away.
                    //Therefore, a popup with a single record form in it cannot be "cancel"ed (closed) without the warning of pending changes.
                    //By passing setting the type for the cancel to "ignore-changes", this will allow us to avoid the confirmation
                    //See, e.g. didCreate_print_job
                    if (payload.type !== 'ignore-changes' && !isReboundEvent) {
                        // This will fail (promise reject) to the catch statement below if anything does not confirm the change.
                        // Otherwise, it will continue and set the new activeRecord.
                        await request(
                            { activeRecord: activeRecordAtStart, newRecord: payload, changeContext: context },
                            { verb: 'confirm', namespace, relation, type: 'changeRecord' },
                            undefined,
                            false // do not error if there are no subscribers
                        );
                    }
                    if (allowStateChange) {
                        const previousActiveRecord = activeRecordRef.current;
                        // The confirmation dialog is asynchronous. If another action opened
                        // a form while it was displayed, this older event must not replace it.
                        if (previousActiveRecord !== activeRecordAtStart) return;
                        const nextActiveRecord = getNextActiveRecord(previousActiveRecord, payload, context, verb);
                        if (!isEqual(previousActiveRecord, nextActiveRecord)) {
                            if (nextActiveRecord.record == null) {
                                logging.debug('[ACTIVE_RECORD] removed.');
                            } else {
                                logging.debug(
                                    `[ACTIVE_RECORD] id: ${nextActiveRecord.record._id}, titular: ${
                                        nextActiveRecord.record.title ?? nextActiveRecord.record.assetNo
                                    }.`
                                );
                            }

                            activeRecordRef.current = nextActiveRecord;
                            setActiveRecord(nextActiveRecord);
                            const lifecycleEvent = getDetailLifecycleEvent(
                                previousActiveRecord,
                                nextActiveRecord,
                                context,
                                verb
                            );
                            if (lifecycleEvent) publish(lifecycleEvent.payload, lifecycleEvent.context);
                        }
                    }
                } catch (err) {
                    logging.info('Active record change canceled');
                    logging.error(err);
                }
            })
        );
        return () => {
            allowStateChange = false;
            unsubscribes.forEach(u => u());
        };
    }, [namespace, relation, type, subscribe, publish, request, currentRoute]);

    //render the provider.
    return rc(ActiveRecordContext.Provider, { value: activeRecord }, children);
}

ActiveRecord.propTypes = {
    namespace: PropTypes.string.isRequired,
    relation: PropTypes.string.isRequired,
    type: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
    isNew: PropTypes.bool
};
export default ActiveRecord;
